import { AlertTriangle } from "lucide-react";
import { cn } from "./_utils";

type HonestyNoticeProps = {
  children: React.ReactNode;
  /** Optional fix affordance — a link, never a blocker. */
  action?: () => void;
  actionLabel?: React.ReactNode;
  className?: string;
};

/**
 * The amber strip — reserved for the product reporting its own weak signals
 * (rubber-stamping, stale data, the Number didn't follow). A nudge, never a
 * blocker. Never used for errors or marketing; do not spend amber on
 * anything else.
 *
 * @example
 * <HonestyNotice actionLabel="Fix →" action={fix}>
 *   2 data sources disconnected — Growth is flying partially blind
 * </HonestyNotice>
 */
export function HonestyNotice({
  children,
  action,
  actionLabel = "Fix →",
  className,
}: HonestyNoticeProps) {
  return (
    <div
      data-testid="honesty-notice"
      className={cn(
        "flex items-center gap-3 px-4 py-3 rounded-[12px]",
        "bg-[var(--honesty-bg)] border border-[var(--honesty-border)]",
        className
      )}
    >
      <AlertTriangle aria-hidden="true" className="h-[15px] w-[15px] shrink-0 text-[var(--honesty)]" />
      <span className="flex-1 text-[13px] leading-[1.4] font-medium text-[var(--honesty-deep)]">
        {children}
      </span>
      {action && (
        <button
          type="button"
          onClick={action}
          className="text-[12.5px] font-semibold text-accent whitespace-nowrap bg-transparent border-none cursor-pointer p-0"
        >
          {actionLabel}
        </button>
      )}
    </div>
  );
}

export type { HonestyNoticeProps };
